import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setAuthFailureHandler } from '../services/api';

const AuthContext = createContext();

const STORAGE_KEY_TOKEN = 'token';
const STORAGE_KEY_USER = 'user';
const STORAGE_KEY_LOGIN_TIME = 'login_time';
const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

const normalizeRole = (role) => {
  const value = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
  if (value === 'superadmin' || value === 'super_admin') return 'super_admin';
  if (value === 'group_admin') return 'admin';
  return value;
};

const normalizeUser = (userData) => {
  if (!userData || typeof userData !== 'object') return userData;
  return { ...userData, role: normalizeRole(userData.role) };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(() => localStorage.getItem(STORAGE_KEY_TOKEN));
  const [user, setUserState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_USER);
      return raw ? normalizeUser(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (token) localStorage.setItem(STORAGE_KEY_TOKEN, token);
    else localStorage.removeItem(STORAGE_KEY_TOKEN);
  }, [token]);

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY_USER);
  }, [user]);

  const login = (newToken, userData) => {
    setTokenState(newToken);
    setUserState(normalizeUser(userData || { username: newToken, role: 'user', user_id: '' }));
    localStorage.setItem(STORAGE_KEY_LOGIN_TIME, Date.now().toString());
  };

  const logout = useCallback(() => {
    setTokenState(null);
    setUserState(null);
    localStorage.removeItem(STORAGE_KEY_LOGIN_TIME);
  }, []);

  // Auto-logout when token expires (1 hour)
  const logoutTimerRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    const loginTime = parseInt(localStorage.getItem(STORAGE_KEY_LOGIN_TIME) || '0', 10);
    if (!loginTime) {
      // Legacy session without login_time — set it now
      localStorage.setItem(STORAGE_KEY_LOGIN_TIME, Date.now().toString());
      return;
    }
    const elapsed = Date.now() - loginTime;
    const remaining = SESSION_TIMEOUT_MS - elapsed;
    if (remaining <= 0) {
      // Already expired
      logout();
      window.location.replace('/login');
      return;
    }
    logoutTimerRef.current = setTimeout(() => {
      logout();
      window.location.replace('/login');
    }, remaining);
    return () => clearTimeout(logoutTimerRef.current);
  }, [token, logout]);

  const setUser = (userData) => {
    setUserState(normalizeUser(userData));
  };

  useEffect(() => {
    setAuthFailureHandler(() => {
      setTokenState(null);
      setUserState(null);
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    });
    return () => setAuthFailureHandler(null);
  }, []);

  const normalizedRole = normalizeRole(user?.role);
  const isSuperAdmin = normalizedRole === 'super_admin';
  const isAdmin = normalizedRole === 'admin' || isSuperAdmin;
  const isGroupAdmin = normalizedRole === 'admin';

  const value = {
    token,
    user,
    login,
    logout,
    setUser,
    isAuthenticated: !!token,
    isSuperAdmin,
    isAdmin,
    isGroupAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
